import { type NextRequest, NextResponse } from "next/server";
import { fallbackLng } from "@/i18n/settings";

export const PATH_FALLBACK = "_";

interface Options {
  previewState: string;
  previewDomain: string;
}

class PathRewriteService {
  private readonly request: NextRequest;
  private readonly headers: Headers;
  private readonly options: Options;

  constructor(request: NextRequest, headers: Headers, options: Options) {
    this.request = request;
    this.headers = headers;
    this.options = {
      previewState: options.previewState || PATH_FALLBACK,
      previewDomain: options.previewDomain || PATH_FALLBACK,
    };
  }

  private rewrite(path: string): NextResponse {
    return NextResponse.rewrite(new URL(path, this.request.url), {
      headers: this.headers,
    });
  }

  private concatPath(segments: string[]) {
    return "/" + segments.filter(Boolean).join("/");
  }

  public brandHomeRewrite(sitePath: string, pagePath: string) {
    const path = sitePath || PATH_FALLBACK;
    const { previewState, previewDomain } = this.options;

    return this.rewrite(
      this.concatPath([
        fallbackLng,
        previewState,
        previewDomain,
        "brands",
        path,
        pagePath,
      ]),
    );
  }

  public audienceHomeRewrite(
    pathPrefix: string,
    sitePath: string,
    pagePath: string,
  ) {
    const path = pathPrefix || PATH_FALLBACK;
    const { previewState, previewDomain } = this.options;

    return this.rewrite(
      this.concatPath([
        fallbackLng,
        previewState,
        previewDomain,
        "audiences",
        path,
        sitePath,
        pagePath,
      ]),
    );
  }

  public eventRewrite(sitePath: string, pagePath: string, lng: string) {
    const lang = lng || fallbackLng;
    const path = sitePath || PATH_FALLBACK;
    const { previewState, previewDomain } = this.options;

    return this.rewrite(
      this.concatPath([
        lang,
        previewState,
        previewDomain,
        "events",
        path,
        lang,
        pagePath,
      ]),
    );
  }

  public courseRewrite(sitePath: string, pagePath: string, lng: string) {
    const lang = lng || fallbackLng;
    const path = sitePath || PATH_FALLBACK;
    const { previewState, previewDomain } = this.options;

    return this.rewrite(
      this.concatPath([
        lang,
        previewState,
        previewDomain,
        "courses",
        path,
        lang,
        pagePath,
      ]),
    );
  }

  public articleRewrite(sitePath: string) {
    const { previewState, previewDomain } = this.options;

    return this.rewrite(
      this.concatPath([
        fallbackLng,
        previewState,
        previewDomain,
        "articles",
        sitePath,
      ]),
    );
  }

  public industryRewrite(sitePath: string) {
    const { previewState, previewDomain } = this.options;

    return this.rewrite(
      this.concatPath([
        fallbackLng,
        previewState,
        previewDomain,
        "industries",
        sitePath,
      ]),
    );
  }
}

export default PathRewriteService;
